import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Music, Mail, Lock, User, Loader2 } from 'lucide-react';
import { pingBackend } from '../utils/warmup';

const AuthPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, isAuthenticated, onboardingCompleted } = useAuth();

  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [slow, setSlow] = useState(false);
  const slowTimerRef = useRef(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: ''
  });

  const landingPath = (userOrFlag) => {
    const completed = typeof userOrFlag === 'boolean'
      ? userOrFlag
      : !!userOrFlag?.onboarding_completed;
    return completed ? '/dashboard' : '/onboarding';
  };

  useEffect(() => { pingBackend(); }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(landingPath(onboardingCompleted), { replace: true });
    }
  }, [isAuthenticated, onboardingCompleted, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSlow(false);
    slowTimerRef.current = setTimeout(() => setSlow(true), 2000);

    try {
      let userData;
      if (isLogin) {
        userData = await login(formData.email, formData.password);
        toast.success(t('auth.loginSuccess'));
      } else {
        userData = await register(formData.email, formData.password, formData.username);
        toast.success(t('auth.registerSuccess'));
      }

      navigate(landingPath(userData), { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.failed'));
    } finally {
      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
      setLoading(false);
      setSlow(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-16 flex items-center justify-center px-6" data-testid="auth-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber to-orange-500 flex items-center justify-center">
            <Music className="w-7 h-7 text-black" />
          </div>
          <span className="font-heading font-bold text-2xl text-white">FretFlow</span>
        </div>

        {/* Card */}
        <div className="bg-[#121212] border border-white/5 rounded-2xl p-8">
          <h1 className="font-heading text-2xl font-bold text-white text-center mb-6">
            {isLogin ? t('auth.login') : t('auth.register')}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="username" className="text-zinc-400">{t('auth.username')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    value={formData.username}
                    onChange={handleChange}
                    required={!isLogin}
                    data-testid="username-input"
                    className="pl-10 bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600"
                    placeholder="JohnDoe"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-400">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="email-input"
                  className="pl-10 bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-400">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="password-input"
                  className="pl-10 bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full bg-gradient-to-r from-amber to-orange-500 text-black font-bold py-6 rounded-full hover:scale-[1.02] transition-transform"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                isLogin ? t('auth.login') : t('auth.register')
              )}
            </Button>

            {loading && slow && (
              <div className="mt-4 flex items-start gap-3 px-4 py-3 rounded-xl bg-amber/10 border border-amber/30 text-sm">
                <Loader2 className="w-4 h-4 mt-0.5 shrink-0 text-amber animate-spin" />
                <div>
                  <p className="text-amber font-semibold mb-0.5">{t('auth.warmupTitle', { defaultValue: 'Il server si sta svegliando' })}</p>
                  <p className="text-zinc-300 text-xs leading-relaxed">{t('auth.warmupDesc', { defaultValue: 'La prima volta del giorno il tier gratuito di Render impiega 30-40 secondi a partire. Le volte successive sarà istantaneo.' })}</p>
                </div>
              </div>
            )}
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-mode"
              className="text-zinc-400 hover:text-amber transition-colors text-sm"
            >
              {isLogin ? t('auth.noAccount') : t('auth.hasAccount')}{' '}
              <span className="text-amber font-semibold">
                {isLogin ? t('auth.register') : t('auth.login')}
              </span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;
